import React, { useState, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import ReCAPTCHA from 'react-google-recaptcha';
import { API_URL } from '../config';


const Signup = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    username: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  React.useEffect(() => {
    if (user) {
      navigate('/account/dashboard');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }
    if (formData.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }
    if (!agreed) {
      return setError('You must agree to the Terms of Service to continue');
    }

    setLoading(true);

    try {
      // Execute the invisible recaptcha to get the token
      const captchaToken = await recaptchaRef.current.executeAsync();
      
      if (!captchaToken) {
        setError('Please complete the reCAPTCHA verification');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          username: formData.username,
          phone: formData.phone,
          password: formData.password,
          captchaToken: captchaToken,
          ref: localStorage.getItem('affiliate_ref') || null
        }),
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          login(data.user, data.token);
          navigate('/account/dashboard');
        }, 1500);
      } else {
        setError(data.error || 'Registration failed. Please try again.');
        setLoading(false);
        // Reset recaptcha on error so user can try again
        recaptchaRef.current.reset();
      }
    } catch (err) {
      setError('Network error. Please make sure the server is running.');
      setLoading(false);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  };

  const handleGoogleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      if (!agreed) {
        return setError('You must agree to the Terms of Service to continue');
      }
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_URL}/api/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenResponse.access_token, ref: localStorage.getItem('affiliate_ref') || null }),
        });
        const data = await response.json();
        
        if (data.success) {
          setSuccess(true);
          setTimeout(() => {
            login(data.user, data.token);
            navigate('/account/dashboard');
          }, 1500);
        } else {
          setError(data.error || 'Google signup failed');
          setLoading(false);
        }
      } catch (err) {
        setError('Network error during Google signup');
        setLoading(false);
      }
    },
    onError: () => setError('Google Signup was unsuccessful'),
  });

  return (
    <main className="min-h-screen relative flex items-center justify-center py-24 px-4">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-[100%] blur-[120px] -z-10 pointer-events-none"></div>

      <div className="w-full max-w-md">
        
        {/* Form Container */}
        <div className="bg-[#131418] border border-white/5 rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 to-transparent"></div>

          <div className="text-center mb-10">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Algo Forex <span className="text-primary">EA</span><br/>
              Create Your Account
            </h1>
            <p className="text-on-surface-variant text-[14px]">
              Join thousands of traders saving up to 80% on professional MQL5 tools.
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl mb-6 text-[14px] text-center">
              {error}
            </div>
          )}

          {!agreed && (
             <div className="bg-red-500/5 border border-red-500/10 text-red-400/80 p-3 rounded-xl mb-6 text-[12px] text-center">
               You must agree to the Terms of Service to continue
             </div>
          )}

          <button 
            onClick={() => handleGoogleSignup()}
            disabled={loading || success}
            type="button" 
            className="w-full bg-[#131418] border border-white/10 hover:border-white/30 transition-colors text-white font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-3 text-[14px] mb-6 disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            Sign Up with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="h-px bg-white/10 flex-1"></div>
            <span className="text-on-surface-variant text-[10px] font-bold tracking-widest uppercase">Or register with email</span>
            <div className="h-px bg-white/10 flex-1"></div>
          </div>

          <form onSubmit={handleSignup} className="space-y-5">
            <div>
              <label className="block text-white text-[13px] font-bold mb-2">Full Name <span className="text-red-500">*</span></label>
              <input 
                type="text" 
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-on-surface-variant focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-[15px]"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-white text-[13px] font-bold mb-2">Username (for Referral Link) <span className="text-red-500">*</span></label>
              <input 
                type="text" 
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className="w-full bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-on-surface-variant focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-[15px]"
                placeholder="trader123"
              />
            </div>

            <div>
              <label className="block text-white text-[13px] font-bold mb-2">Email Address <span className="text-red-500">*</span></label>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-on-surface-variant focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-[15px]"
                placeholder="trader@example.com"
              />
            </div>

            <div>
              <label className="block text-white text-[13px] font-bold mb-2">Phone Number <span className="text-on-surface-variant font-normal">(optional)</span></label>
              <input 
                type="tel" 
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-on-surface-variant focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-[15px]"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-white text-[13px] font-bold mb-2">Password <span className="text-red-500">*</span></label>
              <input 
                type="password" 
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="6"
                className="w-full bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-on-surface-variant focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-[15px]"
                placeholder="At least 6 characters"
              />
            </div>

            <div>
              <label className="block text-white text-[13px] font-bold mb-2">Confirm Password <span className="text-red-500">*</span></label>
              <input 
                type="password" 
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                minLength="6"
                className="w-full bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-on-surface-variant focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-[15px]"
                placeholder="Repeat password"
              />
            </div>

            <div className="flex items-start gap-3 pt-2">
              <input 
                type="checkbox" 
                id="terms"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 bg-[#0B0E11] border-white/20 rounded text-primary focus:ring-primary focus:ring-offset-[#131418]"
              />
              <label htmlFor="terms" className="text-on-surface-variant text-[13px] leading-relaxed">
                I agree to the <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>, <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>, and <Link to="/refund-policy" className="text-primary hover:underline">Refund Policy</Link>.
              </label>
            </div>

            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
              theme="dark"
            />

            <button 
              type="submit" 
              disabled={loading || success}
              className={`w-full rounded-xl px-8 py-3.5 font-bold text-[15px] shadow-lg transition-all duration-300 disabled:opacity-50 mt-4 ${
                success ? 'bg-green-500 text-white shadow-green-500/20 scale-[1.02]' : 'bg-primary text-[#131418] shadow-primary/20 hover:opacity-90'
              }`}
            >
              {success ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-[20px]">check_circle</span>
                  Success! Redirecting...
                </span>
              ) : loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-[20px] animate-spin">autorenew</span>
                  Creating Account...
                </span>
              ) : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-on-surface-variant text-[14px] mt-8">
            Already have an account? <Link to="/login" className="text-primary font-bold hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </main>
  );
};

export default Signup;
